import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import React from 'react';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { COLOR_PRIMARY_1 } from 'src/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { COLOR_PRIMARY, COLOR_TOOLTIP_BACKGROUND } from 'src/const';
import { getFormattedRank } from '../helper';
import { abbreviateNumber } from 'src/utils/functions';

const CustomScoreCardTooltip = ({
  isMarketDataAccessible,
  marketName,
  col1Title,
  col1Val,
  col1TooltipObj,
  col2Title,
  col2Val,
  col2TooltipObj,
  col3Title,
  col3Val,
  col3TooltipObj,
  handleOnSampleClick,
  countryCode,
  isFromDiscoveryAi,
}: {
  isMarketDataAccessible: boolean;
  marketName: string;
  col1Title: string;
  col1Val: string | number;
  col1TooltipObj?;
  col2Title: string;
  col2Val: string | number;
  col2TooltipObj?;
  col3Title: string;
  col3Val: string | number;
  col3TooltipObj?;
  handleOnSampleClick;
  countryCode: string;
  isFromDiscoveryAi?: boolean;
}) => {
  const premiumTitles = isFromDiscoveryAi
    ? [
        'Property Price',
        'Average Revenue',
        'Average Expenses',
        'Property Price Change',
        'Polulation Change',
        'Income Change',
        'Regulation',
        'Occupancy Rate',
        'Daily Rate',
      ]
    : [
        'Property Price',
        'Average Revenue',
        'Average Expenses',
        'Property Price Change',
        'Polulation Change',
        'Income Change',
      ];

  let chipTextCol1: string | number = '';
  let chipTextCol2: string | number = '';
  let chipTextCol3: string | number = '';
  let infoText: string = '';

  if (countryCode === 'US') {
    chipTextCol1 =
      col1Title.toLowerCase().trim() === 'Property Price Change'.toLowerCase()
        ? '2017 - 2022'
        : '2023';
    chipTextCol2 = col2Title.trim() === 'Polulation Change' ? '2017 - 2022' : '2023';
    chipTextCol3 = col3Title.trim() === 'Income Change' ? '2017 - 2022' : '2023';
    infoText =
      col1Title !== 'Property Price Change'
        ? '* Property Price data source: Zillow'
        : '* Data source: US Census';
  } else if (countryCode === 'GB') {
    chipTextCol1 =
      col1Title.toLowerCase().trim() === 'Property Price Change'.toLowerCase()
        ? '2018 - 2023'
        : '2023';
    chipTextCol2 = col2Title.trim() === 'Polulation Change' ? '2016 - 2021' : '2023';
    chipTextCol3 = col3Title.trim() === 'Income Change' ? '2016 - 2021' : '2023';
    infoText =
      col1Title !== 'Property Price Change'
        ? '* Property Price data source: UK land registry'
        : '* Data source: UK Census and UK land registry';
  } else if (countryCode === 'AU') {
    chipTextCol1 =
      col1Title.toLowerCase().trim() === 'Property Price Change'.toLowerCase()
        ? '2018 - 2023'
        : '2023';
    chipTextCol2 = col2Title.trim() === 'Polulation Change' ? '2016 - 2021' : '2023';
    chipTextCol3 = col3Title.trim() === 'Income Change' ? '2016 - 2021' : '2023';
    infoText =
      col1Title !== 'Property Price Change'
        ? '* Property Price data source: Domain'
        : '* Data source: Australia Census';
  } else if (countryCode === 'CA') {
    chipTextCol1 =
      col1Title.toLowerCase().trim() === 'Property Price Change'.toLowerCase()
        ? '2016 - 2021'
        : '2023';
    chipTextCol2 = col2Title.trim() === 'Polulation Change' ? '2016 - 2021' : '2023';
    chipTextCol3 = col3Title.trim() === 'Income Change' ? '2016 - 2021' : '2023';
    infoText =
      col1Title !== 'Property Price Change'
        ? '* Property Price data source: Zillow'
        : '* Data source: Canada Census';
  }

  const tabWithInfoText: boolean = col1Title === 'Property Price Change';

  const makeTooltip = (
    valueObj:
      | {
          rank: number;
          totalMarkets: number;
          nationalAverageComparision: number;
        }
      | undefined
  ) => {
    if (!valueObj) return;

    const isLocked = !valueObj.rank || isNaN(valueObj.rank);

    return (
      <InfoTooltip
        title={
          <Card>
            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
              <Grid container direction="column" alignItems="flex-start">
                <Typography style={{ fontSize: '13px', textAlign: 'left', fontWeight: 400 }}>
                  Ranked{' '}
                  <b style={{ fontWeight: 500, filter: isLocked ? 'blur(4px)' : '' }}>
                    {isLocked ? '250-300' : getFormattedRank(valueObj.rank)}
                  </b>{' '}
                  out of{' '}
                  <b style={{ fontWeight: 500 }}>{abbreviateNumber(valueObj.totalMarkets)}</b>{' '}
                  markets <br />
                  <b style={{ fontWeight: 500, filter: isLocked ? 'blur(4px)' : '' }}>
                    {isLocked ? '56.72%' : Math.abs(valueObj.nationalAverageComparision)}%{' '}
                    {isLocked
                      ? 'above'
                      : valueObj.nationalAverageComparision < 0
                      ? 'below'
                      : 'above'}{' '}
                  </b>{' '}
                  National Average
                </Typography>
              </Grid>
            </CardContent>
          </Card>
        }
        enterTouchDelay={0}
        leaveTouchDelay={10000}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: '15px', color: COLOR_PRIMARY, transform: 'translateY(3px)' }}
        />
      </InfoTooltip>
    );
  };
  return (
    <Grid container xs={12} sx={{ width: '100%' }}>
      <Typography
        variant="body1"
        sx={{
          color: COLOR_PRIMARY_1,
          height: '20px',
          padding: '10px',
          maxWidth: '300px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        Market:<span style={{ color: '#000' }}>{' ' + marketName}</span>
      </Typography>

      <Grid container xs={12} sx={{ height: tabWithInfoText ? '115px' : '100px', width: '100%' }}>
        <Grid item sm={4} sx={{ height: '100%', width: '100px', padding: '5px' }}>
          <Box
            sx={{
              backgroundColor: '#f8f8f8',
              padding: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                height: '12px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  backgroundColor: '#ffff',
                  borderRadius: '10px',
                  padding: ' 0px 5px',
                  fontWeight: 500,
                }}
              >
                {chipTextCol1}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                borderRadius: '10px',
                height: tabWithInfoText ? '70px' : '60px',
                paddingTop: tabWithInfoText ? '10px' : '5px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isMarketDataAccessible && premiumTitles.includes(col1Title) ? 0 : 500,
                  textAlign: 'left',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  filter:
                    isMarketDataAccessible && premiumTitles.includes(col1Title)
                      ? 'blur(3.5px) '
                      : 'blur(0px)',
                }}
                onClick={
                  isMarketDataAccessible && premiumTitles.includes(col1Title)
                    ? handleOnSampleClick
                    : null
                }
              >
                {col1Val}
              </Typography>

              <Box>
                <Typography style={{ fontSize: '12px', textAlign: 'left' }}>
                  {col1Title} {makeTooltip(col1TooltipObj)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          sm={4}
          sx={{
            height: '100%',
            width: '100px',
            padding: '5px',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#f8f8f8',
              padding: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                height: '12px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  backgroundColor: '#ffff',
                  borderRadius: '10px',
                  padding: '0px 5px',
                  fontWeight: 500,
                }}
              >
                {chipTextCol2}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                borderRadius: '10px',
                height: tabWithInfoText ? '70px' : '60px',
                paddingTop: tabWithInfoText ? '10px' : '5px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isMarketDataAccessible && premiumTitles.includes(col2Title) ? 0 : 500,
                  textAlign: 'left',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  filter:
                    isMarketDataAccessible && premiumTitles.includes(col2Title)
                      ? 'blur(3.5px) '
                      : 'blur(0px)',
                }}
                onClick={
                  isMarketDataAccessible && premiumTitles.includes(col2Title)
                    ? handleOnSampleClick
                    : null
                }
              >
                {col2Val}
              </Typography>
              <Typography style={{ fontSize: '12px', textAlign: 'left' }}>
                {col2Title} {makeTooltip(col2TooltipObj)}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item sm={4} sx={{ height: '100%', width: '100px', padding: '5px' }}>
          <Box
            sx={{
              backgroundColor: '#f8f8f8',
              padding: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                height: '12px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  backgroundColor: '#ffff',
                  borderRadius: '10px',
                  padding: '0px 5px',
                  fontWeight: 500,
                }}
              >
                {chipTextCol3}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                borderRadius: '10px',
                height: tabWithInfoText ? '70px' : '60px',
                paddingTop: tabWithInfoText ? '10px' : '5px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isMarketDataAccessible && premiumTitles.includes(col3Title) ? 0 : 500,
                  textAlign: 'left',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  filter:
                    isMarketDataAccessible && premiumTitles.includes(col3Title)
                      ? 'blur(3.5px) '
                      : 'blur(0px)',
                }}
                onClick={
                  isMarketDataAccessible && premiumTitles.includes(col3Title)
                    ? handleOnSampleClick
                    : null
                }
              >
                {col3Val}
              </Typography>
              <Typography style={{ fontSize: '12px', textAlign: 'left' }}>
                {col3Title} {makeTooltip(col3TooltipObj)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ height: '10px' }}>
        <Typography
          sx={{ fontSize: '10px', textAlign: 'right', position: 'relative', top: '-5px' }}
        >
          {col1Title !== 'Regulation' ? infoText : ''}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default CustomScoreCardTooltip;
